import { NavLink, Navigate, Route, Routes } from 'react-router-dom';
import { Spinner, ToastProvider } from './components/ui';
import { AuthProvider, useAuth } from './lib/auth';
import { ROLE_LABEL } from './lib/types';
import { LoginPage } from './pages/LoginPage';
import { ProjectsPage } from './pages/ProjectsPage';
import { ProjectLayout } from './pages/ProjectLayout';
import { ImportPage } from './pages/ImportPage';
import { ContentPage } from './pages/ContentPage';
import { DesignPage } from './pages/DesignPage';
import { PreviewPage } from './pages/PreviewPage';
import { HistoryPage } from './pages/HistoryPage';
import { TemplatesPage } from './pages/TemplatesPage';
import { TemplateEditPage } from './pages/TemplateEditPage';
import { UsersPage } from './pages/UsersPage';
import { ActivityPage } from './pages/ActivityPage';
import { HelpPage } from './pages/HelpPage';
import { AccountPage } from './pages/AccountPage';

export function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <Shell />
      </AuthProvider>
    </ToastProvider>
  );
}

function Shell() {
  const { user, loading, signOut, can } = useAuth();

  if (loading) {
    return <div className="center-screen"><Spinner /></div>;
  }
  if (!user) return <LoginPage />;

  const nav = ({ isActive }: { isActive: boolean }) => (isActive ? 'active' : '');

  return (
    <div className="shell">
      <aside className="sidebar">
        <NavLink to="/projects" className="brand-link" aria-label="Wheels and Wonder — booklets">
          <img src="/brand/wnw-logo-white-vertical.svg" alt="Wheels and Wonder" />
        </NavLink>
        <span className="brand-app">Booklet generator</span>

        <nav>
          <NavLink to="/projects" className={nav}><IconBook /> Booklets</NavLink>
          {can('admin') && <NavLink to="/templates" className={nav}><IconLayout /> Templates</NavLink>}
          {can('admin') && <NavLink to="/users" className={nav}><IconUsers /> Users</NavLink>}
          {can('admin') && <NavLink to="/activity" className={nav}><IconActivity /> Activity</NavLink>}
          <NavLink to="/help" className={nav}><IconHelp /> Help</NavLink>
        </nav>

        <div className="spacer" />

        <div className="sidebar-foot">
          <NavLink to="/account" className={({ isActive }) => `user-chip ${isActive ? 'active' : ''}`} title="Your account">
            <span className="avatar">{(user.name || user.username).slice(0, 1).toUpperCase()}</span>
            <span className="who">
              <span>{user.name || user.username}</span>
              <span className="role">{ROLE_LABEL[user.role]}</span>
            </span>
          </NavLink>
          <button className="btn small ghost" onClick={() => void signOut()}>Sign out</button>
        </div>
      </aside>

      <main className="page-wrap">
        <Routes>
          <Route path="/" element={<Navigate to="/projects" replace />} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/projects/:id" element={<ProjectLayout />}>
            <Route index element={<Navigate to="preview" replace />} />
            <Route path="import" element={<ImportPage />} />
            <Route path="content" element={<ContentPage />} />
            <Route path="design" element={<DesignPage />} />
            <Route path="preview" element={<PreviewPage />} />
            <Route path="history" element={<HistoryPage />} />
            <Route path="versions" element={<Navigate to="../preview" replace />} />
          </Route>
          <Route path="/templates" element={can('admin') ? <TemplatesPage /> : <Navigate to="/projects" replace />} />
          <Route path="/templates/:id" element={can('admin') ? <TemplateEditPage /> : <Navigate to="/projects" replace />} />
          <Route path="/users" element={can('admin') ? <UsersPage /> : <Navigate to="/projects" replace />} />
          <Route path="/activity" element={can('admin') ? <ActivityPage /> : <Navigate to="/projects" replace />} />
          <Route path="/help" element={<HelpPage />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="*" element={<div className="panel">Page not found.</div>} />
        </Routes>
      </main>
    </div>
  );
}

/* Line icons for the side rail, in the same weight as the rest of the UI. */
const svg = {
  width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none',
  stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

const IconBook = () => (
  <svg {...svg}><path d="M4 5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v16l-6-3-6 3Z" /><path d="M16 7h2a2 2 0 0 1 2 2v12" /></svg>
);
const IconLayout = () => (
  <svg {...svg}><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 21V9" /></svg>
);
const IconUsers = () => (
  <svg {...svg}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /></svg>
);
const IconActivity = () => (
  <svg {...svg}><path d="M22 12h-4l-3 9L9 3l-3 9H2" /></svg>
);
const IconHelp = () => (
  <svg {...svg}><circle cx="12" cy="12" r="10" /><path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3" /><path d="M12 17h.01" /></svg>
);
